// ─── Admin Panel API Wrappers ─────────────────────────────────────────────
// Add these to your existing api.ts or import from this file.
// Uses the same axios instance and auth pattern as the rest of the app.

import axios from "axios";
import type {
  SystemSetting, Vendor, VendorGLKeyword, VendorJobKeyword, ItemLookup, Approver, RoutingRule,
  GLAccount, Job, JobGLPermission, GLOverrideRule, SyncResult, TierConfig, TierConfigValue,
  ErrorLogEntry, TroubleshooterResponse, AuditResponse,
} from "./adminTypes";

// Exported so ccReconApi.ts can share this instance and the token interceptor
// without duplicating the refresh logic.
export const API = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || "http://localhost:7071/api",
});

// Token refresh callback — set by AuthContext after login so the interceptor
// can silently acquire a fresh ID token before every admin request.
// MSAL returns the cached token instantly when it hasn't expired yet, so
// there is no performance cost on the happy path.
let _getToken: (() => Promise<string>) | null = null;

export function setTokenRefresher(getter: () => Promise<string>) {
  _getToken = getter;
}

// Kept for the initial token set immediately after login (avoids a race
// where the first request fires before the interceptor has a getter).
export function setAdminAuthToken(token: string) {
  API.defaults.headers.common["Authorization"] = `Bearer ${token}`;
}

// Before every admin request, ask MSAL for a fresh token.
// Falls back silently to whatever header is already set if the getter fails.
API.interceptors.request.use(async (config) => {
  if (_getToken) {
    try {
      const freshToken = await _getToken();
      config.headers["Authorization"] = `Bearer ${freshToken}`;
    } catch {
      // MSAL couldn't refresh — keep the existing header; backend will 401
      // if it's truly expired and the user will be prompted to re-login.
    }
  }
  return config;
});

// ── System Settings ──────────────────────────────────────────────────────

export async function getSettings(): Promise<SystemSetting[]> {
  const { data } = await API.get("/mgmt/settings");
  return data;
}

export async function updateSetting(key: string, setting_value: string): Promise<SystemSetting> {
  const { data } = await API.put(`/mgmt/settings/${key}`, { setting_value });
  return data;
}

export async function createSetting(setting: {
  setting_key: string;
  setting_value: string;
  value_type: string;
  category: string;
  description?: string;
}): Promise<SystemSetting> {
  const { data } = await API.post("/mgmt/settings", setting);
  return data;
}

// ── Vendors ──────────────────────────────────────────────────────────────

export async function getVendors(): Promise<Vendor[]> {
  const { data } = await API.get("/mgmt/vendors");
  return data;
}

export async function getVendor(vendorNumber: number): Promise<Vendor> {
  const { data } = await API.get(`/mgmt/vendors/${vendorNumber}`);
  return data;
}

export async function updateVendor(vendorNumber: number, updates: Partial<Vendor>): Promise<Vendor> {
  const { data } = await API.put(`/mgmt/vendors/${vendorNumber}`, updates);
  return data;
}

export async function syncVendors(): Promise<SyncResult> {
  const { data } = await API.post("/mgmt/vendors/sync");
  return data;
}

// ── Vendor GL Keywords ───────────────────────────────────────────────────

export async function getVendorGLKeywords(vendorNumber?: number): Promise<VendorGLKeyword[]> {
  const params = vendorNumber ? { vendor_number: vendorNumber } : {};
  const { data } = await API.get("/mgmt/vendor-gl-keywords", { params });
  return data;
}

export async function createVendorGLKeyword(keyword: Partial<VendorGLKeyword>): Promise<VendorGLKeyword> {
  const { data } = await API.post("/mgmt/vendor-gl-keywords", keyword);
  return data;
}

export async function updateVendorGLKeyword(id: number, updates: Partial<VendorGLKeyword>): Promise<VendorGLKeyword> {
  const { data } = await API.put(`/mgmt/vendor-gl-keywords/${id}`, updates);
  return data;
}

export async function deleteVendorGLKeyword(id: number): Promise<{ success: boolean }> {
  const { data } = await API.delete(`/mgmt/vendor-gl-keywords/${id}`);
  return data;
}

// ── Vendor Job Keywords ───────────────────────────────────────────────────

export async function getVendorJobKeywords(vendorNumber?: number): Promise<VendorJobKeyword[]> {
  const params = vendorNumber ? { vendor_number: vendorNumber } : {};
  const { data } = await API.get("/mgmt/vendor-job-keywords", { params });
  return data;
}

export async function createVendorJobKeyword(kw: Partial<VendorJobKeyword>): Promise<VendorJobKeyword> {
  const { data } = await API.post("/mgmt/vendor-job-keywords", kw);
  return data;
}

export async function updateVendorJobKeyword(id: number, updates: Partial<VendorJobKeyword>): Promise<VendorJobKeyword> {
  const { data } = await API.put(`/mgmt/vendor-job-keywords/${id}`, updates);
  return data;
}

export async function deleteVendorJobKeyword(id: number): Promise<{ success: boolean }> {
  const { data } = await API.delete(`/mgmt/vendor-job-keywords/${id}`);
  return data;
}

// ── Item Lookup (Parts) ───────────────────────────────────────────────────

export async function getItemLookup(vendorNumber?: number): Promise<ItemLookup[]> {
  const params = vendorNumber ? { vendor_number: vendorNumber } : {};
  const { data } = await API.get("/mgmt/item-lookup", { params });
  return data;
}

export async function createItemLookup(item: Partial<ItemLookup>): Promise<ItemLookup> {
  const { data } = await API.post("/mgmt/item-lookup", item);
  return data;
}

export async function updateItemLookup(id: number, updates: Partial<ItemLookup>): Promise<ItemLookup> {
  const { data } = await API.put(`/mgmt/item-lookup/${id}`, updates);
  return data;
}

export async function deleteItemLookup(id: number): Promise<{ success: boolean }> {
  const { data } = await API.delete(`/mgmt/item-lookup/${id}`);
  return data;
}

// ── Approvers ────────────────────────────────────────────────────────────

export async function getApprovers(): Promise<Approver[]> {
  const { data } = await API.get("/mgmt/approvers");
  return data;
}

export async function updateApprover(id: number, updates: Partial<Approver>): Promise<Approver> {
  const { data } = await API.put(`/mgmt/approvers/${id}`, updates);
  return data;
}

// ── Routing Rules ────────────────────────────────────────────────────────

export async function getRoutingRules(): Promise<RoutingRule[]> {
  const { data } = await API.get("/mgmt/routing-rules");
  return data;
}

export async function createRoutingRule(rule: Partial<RoutingRule>): Promise<RoutingRule> {
  const { data } = await API.post("/mgmt/routing-rules", rule);
  return data;
}

export async function updateRoutingRule(id: number, updates: Partial<RoutingRule>): Promise<RoutingRule> {
  const { data } = await API.put(`/mgmt/routing-rules/${id}`, updates);
  return data;
}

export async function deleteRoutingRule(id: number): Promise<{ success: boolean }> {
  const { data } = await API.delete(`/mgmt/routing-rules/${id}`);
  return data;
}

// ── GL Accounts ──────────────────────────────────────────────────────────

export async function getAdminGLAccounts(): Promise<GLAccount[]> {
  const { data } = await API.get("/mgmt/gl-accounts");
  return data;
}

export async function updateGLAccount(id: number, updates: Partial<GLAccount>): Promise<GLAccount> {
  const { data } = await API.put(`/mgmt/gl-accounts/${id}`, updates);
  return data;
}

export async function createGLAccount(payload: Partial<GLAccount>): Promise<{ id: number }> {
  const { data } = await API.post("/mgmt/gl-accounts", payload);
  return data;
}

export async function deleteGLAccount(id: number): Promise<void> {
  await API.delete(`/mgmt/gl-accounts/${id}`);
}

// ── Jobs ─────────────────────────────────────────────────────────────────

export async function getAdminJobs(): Promise<Job[]> {
  const { data } = await API.get("/mgmt/jobs");
  return data;
}

export async function updateJob(jobNumber: string, updates: Partial<Job>): Promise<Job> {
  const { data } = await API.put(`/mgmt/jobs/${jobNumber}`, updates);
  return data;
}

export async function syncJobs(): Promise<SyncResult> {
  const { data } = await API.post("/mgmt/jobs/sync");
  return data;
}

// ── Job GL Permissions ───────────────────────────────────────────────────

export async function getJobGLPermissions(jobNumber?: string): Promise<JobGLPermission[]> {
  const params = jobNumber ? { job_number: jobNumber } : {};
  const { data } = await API.get("/mgmt/job-gl-permissions", { params });
  return data;
}

export async function createJobGLPermission(perm: { job_number: string; gl_number: string; is_default?: boolean }): Promise<JobGLPermission> {
  const { data } = await API.post("/mgmt/job-gl-permissions", perm);
  return data;
}

export async function deleteJobGLPermission(id: number): Promise<{ success: boolean }> {
  const { data } = await API.delete(`/mgmt/job-gl-permissions/${id}`);
  return data;
}

// ── GL Override Rules ────────────────────────────────────────────────────

export async function getGLOverrideRules(): Promise<GLOverrideRule[]> {
  const { data } = await API.get("/mgmt/gl-override-rules");
  return data;
}

export async function createGLOverrideRule(rule: Partial<GLOverrideRule>): Promise<GLOverrideRule> {
  const { data } = await API.post("/mgmt/gl-override-rules", rule);
  return data;
}

export async function updateGLOverrideRule(id: number, updates: Partial<GLOverrideRule>): Promise<GLOverrideRule> {
  const { data } = await API.put(`/mgmt/gl-override-rules/${id}`, updates);
  return data;
}

export async function deleteGLOverrideRule(id: number): Promise<{ success: boolean }> {
  const { data } = await API.delete(`/mgmt/gl-override-rules/${id}`);
  return data;
}

// ── Tier Config ──────────────────────────────────────────────────────────

export async function getTierConfig(): Promise<TierConfig[]> {
  const { data } = await API.get("/mgmt/tier-config");
  return data;
}

export async function createTierConfig(config: { tier_id: number; tier_name: string; maps_to_field?: string | null; use_in_processing?: boolean }): Promise<{ id: number }> {
  const { data } = await API.post("/mgmt/tier-config", config);
  return data;
}

export async function updateTierConfig(tierId: number, updates: Partial<TierConfig>): Promise<{ success: boolean }> {
  const { data } = await API.put(`/mgmt/tier-config/${tierId}`, updates);
  return data;
}

export async function deleteTierConfig(tierId: number): Promise<{ success: boolean }> {
  const { data } = await API.delete(`/mgmt/tier-config/${tierId}`);
  return data;
}

// ── Tier Config Values ───────────────────────────────────────────────────────

export async function getTierConfigValues(tierId?: number): Promise<TierConfigValue[]> {
  const params = tierId !== undefined ? { tier_id: tierId } : {};
  const { data } = await API.get("/mgmt/tier-config-values", { params });
  return data;
}

export async function createTierConfigValue(value: { tier_id: number; tier_value: number; tier_value_description: string }): Promise<{ id: number }> {
  const { data } = await API.post("/mgmt/tier-config-values", value);
  return data;
}

export async function updateTierConfigValue(id: number, updates: Partial<TierConfigValue>): Promise<{ success: boolean }> {
  const { data } = await API.put(`/mgmt/tier-config-values/${id}`, updates);
  return data;
}

export async function deleteTierConfigValue(id: number): Promise<{ success: boolean }> {
  const { data } = await API.delete(`/mgmt/tier-config-values/${id}`);
  return data;
}

// ── Invoice Sync ──────────────────────────────────────────────────────────────

export async function syncInvoicesFromWinTeam(params: {
  date_from: string;
  date_to: string;
  excluded_vendor_numbers: number[];
}): Promise<{
  success: boolean;
  total_from_winteam: number;
  inserted: number;
  duplicates: number;
  excluded: number;
  errors: number;
  error_details: Array<{ invoice_number: string; vendor_number: number; error: string }>;
}> {
  const { data } = await API.post('/mgmt/invoices/sync', params);
  return data;
}

// ── Error Log ─────────────────────────────────────────────────────────────────

export async function getErrorLog(params: {
  from_date?: string;
  to_date?: string;
  status?: string;
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<{ entries: ErrorLogEntry[]; total_count: number }> {
  const { data } = await API.get('/mgmt/error-log', { params });
  return data;
}

// ── AI Troubleshooter (Session 61 — Phase 1) ──────────────────────────────────

export async function troubleshootInvoice(
  tracking_id: string,
  options?: { model?: string; verbose?: boolean; user_hints?: string }
): Promise<TroubleshooterResponse> {
  const { data } = await API.post('/mgmt/troubleshoot-invoice', {
    tracking_id,
    ...options,
  });
  return data;
}

// ── AI Troubleshooter (Session 62 — Phase 2) ──────────────────────────────────

export async function reprocessInvoiceAudit(
  tracking_id: string,
  options?: { model?: string; verbose?: boolean; user_hints?: string }
): Promise<AuditResponse> {
  const { data } = await API.post('/mgmt/reprocess-invoice-audit', {
    tracking_id,
    ...options,
  });
  return data;
}
